import { isWsEventName, type WsEnvelope, type WsEventName } from "@wato/event-bus";

/** The subset of the browser `WebSocket` API this client uses — real `WebSocket` satisfies it
 * as-is; tests inject a fake implementing just this. */
export interface WebSocketLike {
  readyState: number;
  close(code?: number, reason?: string): void;
  send(data: string): void;
  onopen: (() => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/** Opaque timer token — a real browser number, Node's `Timeout`, or a test fake's own object.
 * Never inspected, only round-tripped to `clearTimeoutImpl`. */
type TimerHandle = unknown;

export type ConnectionState = "connecting" | "open" | "reconnecting" | "polling" | "closed";

interface SubscribeKey {
  readonly workspaceId: string;
  readonly conversationId?: string;
  readonly topic?: "whisper";
}

export interface RealtimeClientOptions {
  /** Resolves the WS URL fresh on every (re)connect attempt (e.g. the session may have
   * rotated, or the app moved workspaces). Same-origin, so no token in the URL — auth is the
   * `wato_dash_sid` cookie the browser attaches to the upgrade request automatically. */
  readonly url: () => string;
  /** Default: the global `WebSocket`. Tests inject a fake. */
  readonly createSocket?: WebSocketFactory;
  /** Called for every envelope the gateway forwards, already validated against
   * `WsEventCatalog`'s known event names (an unknown `event` is dropped, not delivered). */
  readonly onEnvelope: (envelope: WsEnvelope<WsEventName>) => void;
  /** Fires whenever the connection's state changes (build step 12: no UI reads this yet, but
   * a future connection-state chrome/component needs a single place to subscribe). */
  readonly onStateChange?: (state: ConnectionState) => void;
  /** Base reconnect delay. Default 500ms. */
  readonly baseBackoffMs?: number;
  /** Reconnect delay ceiling. Default 15000ms. */
  readonly maxBackoffMs?: number;
  /** If no attempt has reached `open` within this long of the *first* connect attempt, switch
   * to polling and stop reconnecting the socket. Default 8000ms. */
  readonly pollFallbackTimeoutMs?: number;
  /** If no frame of any kind (event, ack or the server's liveness ping) arrives for this long
   * while the page is visible, the socket is presumed half-open: close it and reconnect.
   * Default 60000ms; 0 = off. */
  readonly idleTimeoutMs?: number;
  /** Visibility seam; default `document.visibilityState !== "hidden"`. */
  readonly isVisible?: () => boolean;
  /** Random jitter source for backoff, `[0, 1)`. Default `Math.random`; tests inject a fixed
   * value for deterministic delays. */
  readonly random?: () => number;
  /** Timer seams for tests (`vi.useFakeTimers` interacts poorly with real `WebSocket`, so
   * tests instead inject a controllable clock here). */
  readonly setTimeoutImpl?: (cb: () => void, delayMs: number) => TimerHandle;
  readonly clearTimeoutImpl?: (handle: TimerHandle) => void;
}

function subscribeKeyId(key: SubscribeKey): string {
  const base = key.conversationId ? `${key.workspaceId}:${key.conversationId}` : key.workspaceId;
  return key.topic ? `${base}#${key.topic}` : base;
}

function isEnvelopeLike(value: unknown): value is { event: string } & Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { event?: unknown }).event === "string"
  );
}

/**
 * WP-124 build steps 10-12: connects on `start()`, auto-resubscribes its remembered
 * `{ workspaceId, conversationId? }` set on every reconnect, backs off exponentially on an
 * unexpected close, and — if the socket never reaches `open` within `pollFallbackTimeoutMs` of
 * the first attempt — gives up on WS silently and reports `"polling"` so the caller can start
 * its own short-interval polling against `GET /v1/workspaces/:id/conversations/:id/events`
 * (this module does not itself poll: the caller already owns that HTTP client and its auth,
 * per `apps/dashboard/src/api/client.ts`).
 */
interface ResolvedOptions extends RealtimeClientOptions {
  readonly baseBackoffMs: number;
  readonly maxBackoffMs: number;
  readonly pollFallbackTimeoutMs: number;
  readonly random: () => number;
}

export class RealtimeClient {
  readonly #options: ResolvedOptions;
  readonly #createSocket: WebSocketFactory;
  readonly #setTimeoutImpl: (cb: () => void, delayMs: number) => TimerHandle;
  readonly #clearTimeoutImpl: (handle: TimerHandle) => void;
  readonly #subscriptions = new Map<string, SubscribeKey>();

  #socket: WebSocketLike | null = null;
  #state: ConnectionState = "closed";
  #attempt = 0;
  #reconnectTimer: TimerHandle | null = null;
  #pollFallbackTimer: TimerHandle | null = null;
  #idleTimer: TimerHandle | null = null;
  #stopped = true;

  constructor(options: RealtimeClientOptions) {
    this.#options = {
      ...options,
      baseBackoffMs: options.baseBackoffMs ?? 500,
      maxBackoffMs: options.maxBackoffMs ?? 15_000,
      pollFallbackTimeoutMs: options.pollFallbackTimeoutMs ?? 8_000,
      // `Math.random` unbound is safe (it reads no `this`); wrapped anyway for symmetry with
      // the two below, which are not safe unbound — see the next comment.
      random: options.random ?? (() => Math.random()),
    };
    this.#createSocket =
      options.createSocket ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
    // A bare `setTimeout`/`clearTimeout` reference, called later as `this.#setTimeoutImpl(...)`,
    // invokes it with `this` bound to the `RealtimeClient` instance instead of `window` —
    // Chromium's implementation is a branded Web IDL method and throws "Illegal invocation" for
    // that (caught the hard way: it aborts the whole render tree via an uncaught `pageerror` in
    // Playwright, not merely a broken timer). Wrapping in an arrow function calls the global
    // unbound from any object, which is always safe.
    this.#setTimeoutImpl = options.setTimeoutImpl ?? ((cb, delay) => setTimeout(cb, delay));
    this.#clearTimeoutImpl =
      options.clearTimeoutImpl ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  }

  get state(): ConnectionState {
    return this.#state;
  }

  start(): void {
    if (!this.#stopped) return;
    this.#stopped = false;
    this.#attempt = 0;
    this.#armPollFallback();
    this.#connect();
  }

  /** Tears the client down for good (no reconnect, no polling fallback). */
  stop(): void {
    this.#stopped = true;
    this.#clearReconnectTimer();
    this.#clearPollFallbackTimer();
    this.#clearIdleTimer();
    this.#socket?.close();
    this.#socket = null;
    this.#setState("closed");
  }

  subscribe(workspaceId: string, conversationId?: string, topic?: "whisper"): void {
    const key: SubscribeKey = {
      workspaceId,
      ...(conversationId ? { conversationId } : {}),
      ...(topic ? { topic } : {}),
    };
    this.#subscriptions.set(subscribeKeyId(key), key);
    this.#send({ action: "subscribe", ...key });
  }

  unsubscribe(workspaceId: string, conversationId?: string, topic?: "whisper"): void {
    const key: SubscribeKey = {
      workspaceId,
      ...(conversationId ? { conversationId } : {}),
      ...(topic ? { topic } : {}),
    };
    this.#subscriptions.delete(subscribeKeyId(key));
    this.#send({ action: "unsubscribe", ...key });
  }

  #send(message: Record<string, unknown>): void {
    if (this.#socket && this.#socket.readyState === 1 /* OPEN */) {
      this.#socket.send(JSON.stringify(message));
    }
    // Not open: nothing to do — `#resubscribeAll` replays every remembered subscription once
    // the next connection opens, so a subscribe() call made while offline is not lost.
  }

  #resubscribeAll(): void {
    for (const key of this.#subscriptions.values()) {
      this.#send({ action: "subscribe", ...key });
    }
  }

  #setState(state: ConnectionState): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#options.onStateChange?.(state);
  }

  #armPollFallback(): void {
    this.#clearPollFallbackTimer();
    this.#pollFallbackTimer = this.#setTimeoutImpl(() => {
      if (this.#stopped || this.#state === "open") return;
      // Silent: no error surfaced to the caller (build step 12) — just stop trying WS and let
      // the caller's own polling take over from here.
      this.#clearReconnectTimer();
      this.#clearIdleTimer();
      this.#socket?.close();
      this.#socket = null;
      this.#setState("polling");
    }, this.#options.pollFallbackTimeoutMs);
  }

  #clearPollFallbackTimer(): void {
    if (this.#pollFallbackTimer !== null) {
      this.#clearTimeoutImpl(this.#pollFallbackTimer);
      this.#pollFallbackTimer = null;
    }
  }

  #clearIdleTimer(): void {
    if (this.#idleTimer !== null) {
      this.#clearTimeoutImpl(this.#idleTimer);
      this.#idleTimer = null;
    }
  }

  /** (Re)starts the dead-socket watchdog; called on open and on every inbound frame. */
  #armIdleTimer(socket: WebSocketLike): void {
    const idleMs = this.#options.idleTimeoutMs ?? 60_000;
    if (idleMs <= 0) return;
    this.#clearIdleTimer();
    this.#idleTimer = this.#setTimeoutImpl(() => {
      this.#idleTimer = null;
      if (this.#stopped || this.#socket !== socket) return;
      const visible =
        this.#options.isVisible?.() ??
        (typeof document === "undefined" || document.visibilityState !== "hidden");
      if (!visible) {
        // Background tabs throttle timers and may legitimately see nothing; check again later.
        this.#armIdleTimer(socket);
        return;
      }
      // Half-open socket: detach first so its eventual onclose is ignored, then reconnect.
      this.#socket = null;
      try {
        socket.close();
      } catch {
        // Already unusable.
      }
      this.#scheduleReconnect();
    }, idleMs);
  }

  #clearReconnectTimer(): void {
    if (this.#reconnectTimer !== null) {
      this.#clearTimeoutImpl(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
  }

  #connect(): void {
    if (this.#stopped) return;
    this.#setState(this.#attempt === 0 ? "connecting" : "reconnecting");
    const socket = this.#createSocket(this.#options.url());
    this.#socket = socket;
    socket.onopen = () => {
      if (this.#stopped || this.#socket !== socket) return;
      this.#attempt = 0;
      this.#clearPollFallbackTimer();
      this.#setState("open");
      this.#armIdleTimer(socket);
      this.#resubscribeAll();
    };
    socket.onmessage = (event) => {
      if (this.#socket === socket && this.#state === "open") this.#armIdleTimer(socket);
      this.#handleMessage(event.data);
    };
    socket.onclose = () => {
      if (this.#stopped || this.#socket !== socket) return;
      this.#socket = null;
      this.#clearIdleTimer();
      this.#scheduleReconnect();
    };
    socket.onerror = () => {
      // `onclose` always follows `onerror` for a WS that failed to open or dropped — the
      // reconnect is scheduled there, not here, so it happens exactly once per failure.
    };
  }

  #handleMessage(raw: string): void {
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isEnvelopeLike(data) || !isWsEventName(data.event)) return;
    this.#options.onEnvelope(data as unknown as WsEnvelope<WsEventName>);
  }

  #scheduleReconnect(): void {
    if (this.#stopped) return;
    if (this.#state !== "polling") this.#setState("reconnecting");
    const { baseBackoffMs, maxBackoffMs, random } = this.#options;
    const exponent = Math.min(this.#attempt, 10);
    const capped = Math.min(baseBackoffMs * 2 ** exponent, maxBackoffMs);
    // Full jitter (AWS-style): spreads reconnect storms out instead of every dropped client
    // retrying in lockstep.
    const delay = Math.floor(capped * random());
    this.#attempt += 1;
    this.#clearReconnectTimer();
    this.#reconnectTimer = this.#setTimeoutImpl(() => {
      this.#reconnectTimer = null;
      this.#connect();
    }, delay);
  }
}
